/**
 * Shipper-side top box on the load-tracking page.
 *
 * Replaces the old generic "Next step" CTA bar once a load is awarded, and
 * mirrors the broker dispatch page's award banner: broker, awarded rate, and
 * status in one teal-tinted card. Keeps the "Message Broker" action; the
 * "View Status" button is intentionally gone (the tracker below is the source).
 */

import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Trophy, MessageSquare, Wrench } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { displayCompanyName } from "@/lib/displayCompanyName";
import { formatPhoneNumber } from "@/lib/formatPhone";

interface Props {
  loadId: string;
  awardedBroker: {
    user_id: string | null;
    company_name: string | null;
    contact_name: string | null;
    email: string | null;
    phone: string | null;
  } | null;
  awardedAmount: number | null;
  miles: number | null;
  statusLabel: string;
  hasDispatch: boolean;
  onMessageBroker?: () => void;
}

export function ShipperAwardedBrokerCard({
  loadId, awardedBroker, awardedAmount, miles, statusLabel, hasDispatch, onMessageBroker,
}: Props) {
  const navigate = useNavigate();
  const rpm = awardedAmount && miles ? (Number(awardedAmount) / miles).toFixed(2) : null;

  return (
    <Card className="p-4 mb-4 border-brand-teal/25 bg-brand-teal/[0.06] shadow-sm">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
        <h3 className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider flex items-center gap-1.5">
          <Trophy className="h-3.5 w-3.5 text-brand-teal" /> Awarded Broker &amp; Rate
        </h3>
        <div className="flex items-center gap-2 flex-wrap">
          {!hasDispatch && (
            <Button
              size="sm"
              className="h-8 gap-1.5 text-xs bg-brand-teal hover:bg-brand-teal/90 text-white"
              onClick={() => navigate(`/lr-direct/dispatches/build/${loadId}`)}
            >
              <Wrench className="h-3.5 w-3.5" /> Build Dispatch
            </Button>
          )}
          {onMessageBroker && (
            <Button size="sm" variant="outline" className="h-8 gap-1.5 text-xs" onClick={onMessageBroker}>
              <MessageSquare className="h-3.5 w-3.5" /> Message Broker
            </Button>
          )}
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-3">
        <div>
          <p className="text-[10px] text-muted-foreground uppercase mb-1">Broker</p>
          <p className="text-sm font-semibold text-foreground">
            {displayCompanyName(awardedBroker?.company_name) || "—"}
          </p>
          {awardedBroker?.contact_name && (
            <p className="text-xs text-muted-foreground">{awardedBroker.contact_name}</p>
          )}
          {(awardedBroker?.email || awardedBroker?.phone) && (
            <p className="text-[11px] text-muted-foreground mt-0.5">
              {awardedBroker?.email}
              {awardedBroker?.email && awardedBroker?.phone ? " · " : ""}
              {awardedBroker?.phone ? formatPhoneNumber(awardedBroker.phone) : ""}
            </p>
          )}
        </div>
        <div>
          <p className="text-[10px] text-muted-foreground uppercase mb-1">Awarded Rate</p>
          <p className="text-lg font-bold text-brand-teal tabular-nums">
            {awardedAmount != null ? `$${Number(awardedAmount).toLocaleString()}` : "—"}
          </p>
          {rpm && (
            <p className="text-xs text-muted-foreground tabular-nums">
              ${rpm}/mi · {miles?.toLocaleString()} mi
            </p>
          )}
        </div>
        <div>
          <p className="text-[10px] text-muted-foreground uppercase mb-1">Status</p>
          <Badge className="bg-brand-teal/10 text-brand-teal border border-brand-teal/30 uppercase text-[10px]">
            {statusLabel}
          </Badge>
        </div>
      </div>
    </Card>
  );
}
